import React, { Component } from 'react';
import { compose } from 'redux';
import classNames from 'classnames';
import { head } from 'lodash/fp';
import request from 'utils/request_ajax';
import { routes } from 'constants/config';
import { Icon } from 'ui';
import { scormUploadToFilestack } from 'misc/scormUpload';
import DropTarget from './dropTarget';

class ScormUploader extends Component {
  constructor(props) {
    super(props);
    this.state = {
      uploading: false,
      uploadComplete: false,
      fileName: null,
      scormId: props.scormId || null,
      uploadProgress: 0,
      processingProgress: 1,
      message: t('contents.index.scorm.upload_file'),
      warning: null,
    };
    this.supportedFormats = /\.zip/i;
    this.createLesson = this.createLesson.bind(this);
    this.uploadScorm = this.uploadScorm.bind(this);
    this.handleScormSuccess = this.handleScormSuccess.bind(this);
    this.startScormUploadJob = this.startScormUploadJob.bind(this);
    this.statusCheck = this.statusCheck.bind(this);
    this.handleUploadFail = this.handleUploadFail.bind(this);
    this.renderUploadStatus = this.renderUploadStatus.bind(this);
    this.trackUploadProgress = this.trackUploadProgress.bind(this);
    this.renderStartUpload = this.renderStartUpload.bind(this);
  }

  isFileValid(file) {
    return this.supportedFormats.test(file.name);
  }

  createLesson(e, files) {
    const { setIds, setUploadFailed } = this.props;
    const originalFile = head(files || e.target.files);
    const sanatizedFilename = originalFile.name.replace(/[^\w.]/gi, '');
    const file = new File([originalFile], sanatizedFilename, {
      type: originalFile.type,
    });

    this.props.resetErrors();
    if (this.isFileValid(file)) {
      this.props.setUploading();
      this.setState(
        {
          uploading: true,
          message: t('contents.index.scorm.package_uploading'),
          fileName: file.name,
        },
        () => {
          const { scormId, lessonId } = this.props;
          if (!scormId && !lessonId) {
            request
              .postJSON(routes.CREATE_LESSON, { content_type: 'Scorm' })
              .then(payload => {
                if (!this.unmounted) {
                  this.setState(
                    {
                      scormId: payload.scorm.id,
                    },
                    () => this.uploadScorm(file),
                  );
                  setIds(payload.id, payload.scorm.id);
                }
              })
              .fail(this.handleUploadFail);
          } else {
            this.uploadScorm(file);
          }
        },
      );
    } else {
      setUploadFailed(t('contents.index.scorm.file_type'));
      if (e) e.target.value = '';
    }
  }

  uploadScorm(file) {
    scormUploadToFilestack(file, this.trackUploadProgress).then(
      this.startScormUploadJob,
    );
  }

  trackUploadProgress(progress) {
    if (progress.totalPercent) {
      const uploadProgress = progress.totalPercent / 2;
      this.setState({ uploadProgress });
    }
  }

  startScormUploadJob(response) {
    const filestackHandle = response.handle;
    const { updating } = this.props;

    this.setState(
      {
        message: t('contents.index.scorm.package_processing'),
      },
      () => {
        request
          .putJSON(routes.SCORM_START(this.state.scormId), {
            filestack_handle: filestackHandle,
            updating,
          })
          .then(payload => {
            if (!this.unmounted && (payload.token || payload.importJobId)) {
              this.statusCheck(payload.token, payload.importJobId, updating);
            } else {
              this.handleUploadFail();
            }
          })
          .fail(this.handleUploadFail);
      },
    );
  }

  handleUploadFail(response) {
    const { setUploadFailed, deleteLesson, updating, setIds } = this.props;

    if (!this.unmounted) {
      const message =
        response.message ||
        response.responseText ||
        t('contents.index.scorm.upload_failed');

      if (!updating) {
        setIds(null, null);
      }

      setUploadFailed(message);
      deleteLesson();

      this.setState({
        uploading: false,
        uploadComplete: false,
        message: t('contents.index.scorm.upload_file'),
        uploadProgress: 0,
        processingProgress: 1,
      });
    }
  }

  statusCheck(token, importJobId, updating) {
    request
      .get(routes.SCORM_STATUS(this.state.scormId), {
        token,
        importJobId,
        updating,
      })
      .then(response => {
        switch (response.status) {
          case 'COMPLETE':
            this.setState({ processingProgress: 50 });
            setTimeout(() => this.handleScormSuccess(response), 2000);
            break;
          case 'RUNNING':
            setTimeout(
              () => this.statusCheck(token, importJobId, updating),
              1000,
            );
            this.setState(prevState => ({
              message: response.message,
              processingProgress: Math.min(
                40,
                prevState.processingProgress + 1,
              ),
            }));
            break;
          default:
            this.handleUploadFail(response);
        }
      })
      .fail(response => {
        const failureMessage = response || {
          error: t('contents.index.scorm.connection_lost'),
        };
        this.handleUploadFail(failureMessage);
      });
  }

  handleScormSuccess(response) {
    if (!this.unmounted && response.successful) {
      this.props.setUploadSuccessful(response.version);
      this.setState({
        uploading: false,
        uploadComplete: true,
        message: t('contents.index.scorm.package_processed'),
        warning: response.warning,
      });
    } else {
      this.handleUploadFail(response);
    }
  }

  renderUploadStatus() {
    const { fileName } = this.state;
    return (
      <div className="ScormUploader-uploadArea ScormUploader-uploadStatus">
        <div>{this.state.message}</div>
        <div className="ScormUploader-fileName fw-b">
          {fileName || t('contents.index.scorm.loading_file_name')}
        </div>
        {this.state.uploading && (
          <div className="ScormUploader-uploadProgress">
            <div
              className="ScormUploader-uploadProgressBar"
              style={{
                width: `${
                  this.state.uploadProgress + this.state.processingProgress
                }%`,
              }}
            />
          </div>
        )}
      </div>
    );
  }

  renderStartUpload() {
    return this.props.connectDropTarget(
      <div className="ScormUploader-uploadArea">
        <div className="ScormUploader-description">{this.state.message}</div>
        <div className="ScormUploader-button NewButton NewButton--center NewButton--white">
          <span>{t('contents.index.scorm.browse')}</span>
          <input
            ref={input => {
              this.fileInput = input;
            }}
            type="file"
            onChange={this.createLesson}
            disabled={this.state.uploading}
          />
        </div>
      </div>,
    );
  }

  componentWillUnmount() {
    this.unmounted = true;
  }

  render() {
    const uploaderClasses = classNames('ScormUploader', {
      'ScormUploader--uploading': this.state.uploading,
      'ScormUploader--uploadSuccess': this.state.uploadComplete,
      'ScormUploader--isOver': this.props.isOver,
    });
    const showUploadStatus = this.state.uploading || this.state.uploadComplete;

    return (
      <div>
        {this.state.warning && (
          <div className="ErrorContainer ErrorContainer--brandYellow Animated--fadeIn mb-8 d-f ai-c">
            <Icon name="warning_sign" className="mr-8" />
            <span className="text-wrap">{this.state.warning}</span>
          </div>
        )}
        <div className={uploaderClasses}>
          {showUploadStatus
            ? this.renderUploadStatus()
            : this.renderStartUpload()}
        </div>
      </div>
    );
  }
}

export default compose(DropTarget)(ScormUploader);
